import { type ReactNode, useState } from 'react';
import { FormField, Input } from './FormField.js';

export interface PickOption {
  id: string;
  label: string;
}

/**
 * Searchable, client-filtered multi-select over a bounded option list. Replaces
 * the old raw comma-separated-ObjectId text inputs for car / car-model
 * restrictions — options come from `/cars` + `/car-models`, so admins pick real
 * records instead of typing 24-char hex ids. Selected ids that are not in
 * `options` (e.g. a since-deleted car) still render as a removable chip by id.
 */
export function IdMultiSelect({
  label,
  name,
  hint,
  options,
  selected,
  onChange,
  loading,
  capNote,
  shown,
  toolbar,
}: {
  label: string;
  name: string;
  hint?: string;
  options: PickOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  capNote?: string;
  /**
   * 383 (#1272): the options the list offers, when narrower than `options` (e.g. one supplier's cars).
   * Chips still label every selected id from the full `options`, so a choice hidden by the narrowing
   * stays visible and removable.
   */
  shown?: PickOption[];
  /** 383: controls rendered between the filter box and the chips (a supplier select-all). */
  toolbar?: ReactNode;
}) {
  const [term, setTerm] = useState('');
  const q = term.trim().toLowerCase();
  const listed = shown ?? options;
  const filtered = q ? listed.filter((o) => o.label.toLowerCase().includes(q)) : listed;
  const byId = new Map(options.map((o) => [o.id, o.label]));

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  return (
    <FormField label={label} name={name} hint={hint}>
      <Input
        id={name}
        placeholder={loading ? 'Loading…' : 'Filter by name…'}
        value={term}
        onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
      />
      {toolbar}
      {selected.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selected.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id)}
              className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-xs text-ink hover:bg-hairline"
            >
              {byId.get(id) ?? id}
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <ul
        data-testid={`${name}-options`}
        className="mt-1 max-h-40 divide-y divide-hairline overflow-y-auto rounded-md border border-hairline bg-canvas text-sm"
      >
        {filtered.length ? (
          filtered.map((o) => (
            <li key={o.id}>
              <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-ink hover:bg-surface">
                <input
                  type="checkbox"
                  checked={selected.includes(o.id)}
                  onChange={() => toggle(o.id)}
                  className="rounded border-hairline text-primary focus:ring-primary"
                />
                {o.label}
              </label>
            </li>
          ))
        ) : (
          <li className="px-3 py-2 text-steel">{loading ? 'Loading…' : 'No matches.'}</li>
        )}
      </ul>
      {capNote && <p className="text-xs text-amber-600">{capNote}</p>}
    </FormField>
  );
}
